// Pure selection logic for the "move candidates" flow.
// A selection is an immutable array of invitation ids shared by every tab.
// Every function returns a new array and never mutates its inputs.

const idOf = (candidate) => candidate.invitation_id;

const movableOf = (candidates = []) => candidates.filter((c) => c.movable);

export const splitByTab = (candidates = []) => ({
  all: candidates,
  consumed: candidates.filter((c) => c.consumed === true),
  notConsumed: candidates.filter((c) => c.consumed !== true),
});

export const toggleCandidate = (selection, candidate, maxSelectable) => {
  if (!candidate.movable) return { selection, limitReached: false };

  const id = idOf(candidate);
  if (selection.includes(id)) {
    return {
      selection: selection.filter((selectedId) => selectedId !== id),
      limitReached: false,
    };
  }
  if (selection.length >= maxSelectable) {
    return { selection, limitReached: true };
  }
  return { selection: [...selection, id], limitReached: false };
};

export const getTabSelectionState = (selection, tabCandidates) => {
  const movableIds = movableOf(tabCandidates).map(idOf);
  const selectedCount = movableIds.filter((id) =>
    selection.includes(id),
  ).length;
  if (selectedCount === 0) return 'none';
  return selectedCount === movableIds.length ? 'all' : 'some';
};

export const toggleAllInTab = (selection, tabCandidates, maxSelectable) => {
  const movableIds = movableOf(tabCandidates).map(idOf);
  if (movableIds.length === 0) return { selection, limitReached: false };

  if (getTabSelectionState(selection, tabCandidates) === 'all') {
    return {
      selection: selection.filter((id) => !movableIds.includes(id)),
      limitReached: false,
    };
  }

  const missing = movableIds.filter((id) => !selection.includes(id));
  const room = Math.max(maxSelectable - selection.length, 0);
  return {
    selection: [...selection, ...missing.slice(0, room)],
    limitReached: missing.length > room,
  };
};

// Re-validates a selection against a fresh preview (destination changed).
// The selection is never trimmed to the cap: `overLimit` tells the UI to
// block the next step until the user adjusts it.
export const reconcileSelection = (selection, candidates, maxSelectable) => {
  const movableIds = movableOf(candidates).map(idOf);
  const kept = selection.filter((id) => movableIds.includes(id));
  return {
    selection: kept,
    removed: selection.filter((id) => !movableIds.includes(id)),
    overLimit: kept.length > maxSelectable,
  };
};

export const selectedCandidates = (selection, candidates = []) =>
  candidates.filter((c) => selection.includes(idOf(c)));

export const countSelected = (selection, candidates) => {
  const { consumed, notConsumed } = splitByTab(
    selectedCandidates(selection, candidates),
  );
  return {
    total: consumed.length + notConsumed.length,
    consumed: consumed.length,
    notConsumed: notConsumed.length,
  };
};
